import { noop } from 'lodash';
import { createContext, memo, type PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ViewTypeName } from '@air/types';

import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useFiltersContext } from '~/providers/FiltersProvider';
import { setCurrentViewTypeAction } from '~/store/configViews/actions';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { useCanViewPrivateAddOn } from '~/swr-hooks/workspaces/addOns/useCanViewPrivateAddOn';
import { DEFAULT_CONTEXT, getLastUsedSearchType, type SearchType, setLastUsedSearchType } from '~/utils/SearchUtils';

export type PrivateSearchProviderProps = {
  supportsSmartSearch: boolean;
};

export type PrivateSearchProviderType = {
  searchType: SearchType;
  setSearchType: (type: SearchType) => void;
  canUseSmartSearch: boolean;
  isSemanticSearchEnabled: boolean;
  isHybridSemanticSearchEnabled: boolean;
  isSearchCommandOpen: boolean;
  setIsSearchCommandOpen: (isOpen: boolean) => void;
};

const defaultValue: PrivateSearchProviderType = {
  searchType: 'keyword',
  setSearchType: noop,
  canUseSmartSearch: false,
  isSemanticSearchEnabled: false,
  isHybridSemanticSearchEnabled: false,
  isSearchCommandOpen: false,
  setIsSearchCommandOpen: noop,
};

export const PrivateSearchContext = createContext<PrivateSearchProviderType>(defaultValue);

export const PrivateSearchProvider = memo(
  ({ children, supportsSmartSearch }: PropsWithChildren<PrivateSearchProviderProps>) => {
    const dispatch = useDispatch();
    const currentViewTypeName = useSelector(currentViewTypeNameSelector);
    const { hybridSemanticSearchEnabled } = useFeatureFlags();
    const { canViewAddOn: canViewSemanticSearch } = useCanViewPrivateAddOn({ key: 'semanticSearch' });
    const [searchType, setSearchType] = useState<SearchType>(defaultValue.searchType);
    const [isSearchCommandOpen, setIsSearchCommandOpen] = useState<boolean>(false);

    // Using the long-form method of computing isSearchActive to avoid circular dependency
    const { search } = useFiltersContext();
    const { filtersCount } = useAppliedFiltersCount();
    const isSearchActive = !!search || filtersCount > 0;

    const canUseSmartSearch = useMemo(() => {
      return supportsSmartSearch && canViewSemanticSearch;
    }, [supportsSmartSearch, canViewSemanticSearch]);

    useEffect(() => {
      if (canUseSmartSearch) {
        setSearchType(getLastUsedSearchType(DEFAULT_CONTEXT));
      }
    }, [canUseSmartSearch]);

    // Within a search session that supports smart search, we want to persist the search type
    useEffect(() => {
      if (canUseSmartSearch) {
        setLastUsedSearchType(searchType, DEFAULT_CONTEXT);
      }
    }, [searchType, canUseSmartSearch]);

    // Smart search is only supported in gallery view
    useEffect(() => {
      if (
        searchType === 'smart' &&
        canUseSmartSearch &&
        currentViewTypeName !== ViewTypeName.gallery &&
        isSearchActive
      ) {
        dispatch(setCurrentViewTypeAction({ viewTypeName: ViewTypeName.gallery }));
      }
    }, [searchType, canUseSmartSearch, dispatch, currentViewTypeName, isSearchActive]);

    const value = useMemo<PrivateSearchProviderType>(
      () => ({
        searchType,
        setSearchType,
        canUseSmartSearch,
        isSemanticSearchEnabled: canUseSmartSearch && searchType === 'smart',
        isHybridSemanticSearchEnabled: canUseSmartSearch && hybridSemanticSearchEnabled && searchType === 'keyword',
        isSearchCommandOpen,
        setIsSearchCommandOpen,
      }),
      [searchType, canUseSmartSearch, isSearchCommandOpen, hybridSemanticSearchEnabled],
    );

    return <PrivateSearchContext.Provider value={value}>{children}</PrivateSearchContext.Provider>;
  },
);

PrivateSearchProvider.displayName = 'PrivateSearchProvider';

export const usePrivateSearchContext = () => useContext(PrivateSearchContext);
